import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { useSeo } from "@/utils/seo";

const FEEDBACK_SUBMIT_ADDR = "https://readdy.ai/api/form/db4neehuu2cnvnf27eq0";

const highlights = [
  {
    icon: "ri-flashlight-line",
    title: "实时更新",
    desc: "热点出现后第一时间跟进，压缩你等待真相的时间。",
  },
  {
    icon: "ri-layout-masonry-line",
    title: "分类清晰",
    desc: "娱乐、明星、社会、影视四大频道，想看什么一目了然。",
  },
  {
    icon: "ri-shield-check-line",
    title: "客观整理",
    desc: "坚持多方信息来源交叉比对，不夸大、不臆断。",
  },
  {
    icon: "ri-bookmark-3-line",
    title: "随手收藏",
    desc: "无需登录，本地收藏感兴趣的内容，方便二次查阅。",
  },
];

export default function AboutPage() {
  useSeo({
    title: "关于我们 - 黑料娱乐八卦与揭秘资讯平台",
    description:
      "了解黑料网站的定位与服务范围：聚焦娱乐八卦、明星隐秘消息与社会热点揭秘，致力于为用户提供及时、客观、可核查的资讯内容。",
    keywords: "关于黑料,黑料网站介绍,娱乐八卦平台,爆料投稿",
    canonicalPath: "/about",
  });

  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setFormStatus("success");
      formEl.reset();
      return;
    }
    formData.delete("contact_alt");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, String(value));
    });

    setFormStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FEEDBACK_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const isSuccess = response.ok && parsed?.code === "OK" && !isSpam;

      if (isSuccess) {
        setFormStatus("success");
        formEl.reset();
      } else {
        setFormStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim()
            ? serverMsg
            : "提交失败，请稍后重试。",
        );
      }
    } catch {
      setFormStatus("error");
      setFormError("网络异常，暂时无法提交，请稍后再试。");
    }
  };

  return (
    <SiteLayout>
      <div className="px-4 md:px-6 py-6 md:py-10 space-y-10 md:space-y-16">
        <section className="relative overflow-hidden rounded-lg bg-foreground-950 text-background-50 px-5 md:px-10 py-10 md:py-16">
          <div className="relative z-10 max-w-3xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-background-50/10 text-xs mb-4">
              <i className="ri-information-line" />
              关于黑料
            </span>
            <h1 className="font-heading font-extrabold text-2xl md:text-4xl leading-tight mb-4">
              把娱乐圈的故事，讲清楚
            </h1>
            <p className="text-sm md:text-base text-background-200/85 leading-relaxed">
              黑料是一个聚焦娱乐八卦、明星隐秘消息与社会热点揭秘的资讯平台。我们聚合影视、综艺、娱乐新闻等多个领域的动态，用清晰的分类、简洁的呈现与客观的整理，帮助关注娱乐圈的你第一时间了解最新内幕。无论是恋情绯闻、片场风云，还是行业潜规则，我们都希望让你看得更快、更明白。
            </p>
          </div>
          <i className="ri-mic-2-line absolute -right-8 -bottom-10 text-[220px] text-background-50/5" />
        </section>

        <section>
          <div className="text-center mb-8">
            <h2 className="font-heading font-extrabold text-xl md:text-3xl mb-2">我们想做的三件事</h2>
            <p className="text-sm text-foreground-500">及时、清晰、可信赖</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {highlights.map((item) => (
              <div
                key={item.title}
                className="p-5 rounded-lg border border-background-200 bg-background-50"
              >
                <span className="w-11 h-11 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center mb-4">
                  <i className={`${item.icon} text-xl`} />
                </span>
                <h3 className="font-heading font-bold text-base mb-2">{item.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div>
            <h2 className="font-heading font-extrabold text-xl md:text-2xl mb-3">联系我们</h2>
            <p className="text-sm text-foreground-600 leading-relaxed mb-5">
              有爆料线索、内容纠错或合作意向？填写右侧表单，我们会认真查看每一条反馈。也欢迎关注我们的社交账号，第一时间获取最新动态。
            </p>
            <ul className="space-y-4">
              <li className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center">
                  <i className="ri-mail-line" />
                </span>
                <div>
                  <p className="text-sm font-medium">投稿与反馈邮箱</p>
                  <p className="text-sm text-foreground-500">contact@heiliao-demo.com</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center">
                  <i className="ri-time-line" />
                </span>
                <div>
                  <p className="text-sm font-medium">内容更新</p>
                  <p className="text-sm text-foreground-500">每日 09:00 - 24:00 持续更新</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center">
                  <i className="ri-map-pin-line" />
                </span>
                <div>
                  <p className="text-sm font-medium">办公地点</p>
                  <p className="text-sm text-foreground-500">中国 · 上海（线上运营）</p>
                </div>
              </li>
            </ul>
          </div>

          <form
            id="feedback-form"
            data-readdy-form
            action={FEEDBACK_SUBMIT_ADDR}
            onSubmit={handleSubmit}
            className="p-5 md:p-6 rounded-lg border border-background-200 bg-background-50"
          >
            <h3 className="font-heading font-bold text-lg mb-1">爆料投稿 / 读者反馈</h3>
            <p className="text-xs text-foreground-500 mb-5">带 * 的为必填项</p>

            <div className="space-y-4">
              <div>
                <label htmlFor="fb-name" className="block text-sm mb-1.5">
                  你的称呼 *
                </label>
                <input
                  id="fb-name"
                  name="name"
                  type="text"
                  required
                  placeholder="怎么称呼你"
                  className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm outline-none focus:border-primary-400 transition"
                />
              </div>
              <div>
                <label htmlFor="fb-email" className="block text-sm mb-1.5">
                  邮箱 *
                </label>
                <input
                  id="fb-email"
                  name="email"
                  type="email"
                  required
                  placeholder="方便我们回复你"
                  className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm outline-none focus:border-primary-400 transition"
                />
              </div>
              <div>
                <label htmlFor="fb-type" className="block text-sm mb-1.5">
                  反馈类型
                </label>
                <select
                  id="fb-type"
                  name="type"
                  defaultValue="爆料投稿"
                  className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm outline-none focus:border-primary-400 transition cursor-pointer"
                >
                  <option value="爆料投稿">爆料投稿</option>
                  <option value="内容纠错">内容纠错</option>
                  <option value="功能建议">功能建议</option>
                  <option value="商务合作">商务合作</option>
                </select>
              </div>
              <div>
                <label htmlFor="fb-message" className="block text-sm mb-1.5">
                  详细内容 *
                </label>
                <textarea
                  id="fb-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  placeholder="请描述你的线索或反馈，最多 500 字"
                  className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm outline-none focus:border-primary-400 transition resize-none"
                />
              </div>

              <input
                type="text"
                name="contact_alt"
                className="field-extra-note"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              {formStatus === "success" && (
                <p className="flex items-center gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-3 py-2.5">
                  <i className="ri-checkbox-circle-line" />
                  提交成功，感谢你的反馈，我们会尽快查看。
                </p>
              )}
              {formStatus === "error" && (
                <p className="flex items-center gap-2 text-sm text-primary-800 bg-primary-100/70 border border-primary-200 rounded-md px-3 py-2.5">
                  <i className="ri-error-warning-line" />
                  {formError}
                </p>
              )}

              <button
                type="submit"
                disabled={formStatus === "submitting"}
                className="w-full py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {formStatus === "submitting" ? "提交中…" : "提交反馈"}
              </button>
            </div>
          </form>
        </section>

        <section className="rounded-lg border border-background-200 bg-background-100/60 p-5 md:p-8">
          <h2 className="font-heading font-extrabold text-lg md:text-xl mb-3 flex items-center gap-2">
            <i className="ri-file-shield-2-line text-primary-600" />
            免责声明与版权说明
          </h2>
          <div className="space-y-3 text-sm text-foreground-600 leading-relaxed">
            <p>
              本站为产品演示站点，所展示的全部标题、人物、事件与图片均属虚构，仅用于页面效果展示，不指向任何真实人物或事件，请勿对号入座。
            </p>
            <p>
              站内文字内容由本站整理编写，转载或引用请注明来源。若您认为本站内容侵犯了您的合法权益，可通过上方表单与我们联系，我们将在核实后及时处理。
            </p>
            <p>
              对于用户通过反馈表单提交的线索，本站会尽力核实，但不对线索的绝对真实性作出保证。请勿在投稿中包含他人隐私信息或未经证实的人身攻击内容。
            </p>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-4-line" />
              返回首页
            </Link>
            <Link
              to="/category/entertainment"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md border border-background-300 text-sm hover:border-primary-300 hover:text-primary-600 transition cursor-pointer whitespace-nowrap"
            >
              浏览最新黑料
            </Link>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}